import { useState, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Fab,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  ClickAwayListener,
  IconButton,
  Chip,
  Autocomplete,
  InputAdornment,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonIcon from '@mui/icons-material/Person'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CloseIcon from '@mui/icons-material/Close'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { api } from '../lib/api'
import type { StudentDTO, AddStudentInput, UpdateStudentInput, TagDTO } from '@school-homestead/shared'
import StudentDrawer from '../components/StudentDrawer'
import DateInput from '../components/DateInput'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(totalMonths: number): string {
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`
  return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`
}

/** Format an ISO date string to MM/DD/YYYY for display. */
function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = dayjs(iso)
  return d.isValid() ? d.format('MM/DD/YYYY') : '—'
}

type SortColumn = 'childName' | 'parentName' | 'childDob' | 'ageInMonths' | 'contactPhone' | 'contactEmail' | 'joinDate' | 'tags'
type SortDirection = 'asc' | 'desc'

interface ColumnDef {
  id: SortColumn
  label: string
  editable: boolean
  align?: 'left' | 'right'
  /** If true, the column is sortable via header click. */
  sortable: boolean
}

const COLUMNS: ColumnDef[] = [
  { id: 'childName', label: 'Name', editable: true, sortable: true },
  { id: 'parentName', label: 'Parent', editable: true, sortable: true },
  { id: 'childDob', label: 'DOB', editable: true, sortable: true },
  { id: 'ageInMonths', label: 'Age', editable: false, sortable: true },
  { id: 'contactPhone', label: 'Phone', editable: true, sortable: true },
  { id: 'contactEmail', label: 'Email', editable: true, sortable: true },
  { id: 'joinDate', label: 'Join Date', editable: true, sortable: true },
  { id: 'tags', label: 'Tags', editable: true, sortable: false },
]

const EMPTY_FORM: AddStudentInput = {
  childName: '',
  childDob: '',
  parentName: '',
  contactPhone: '',
  contactEmail: '',
  joinDate: '',
  notes: '',
}

// ---------------------------------------------------------------------------
// Tags Cell
// ---------------------------------------------------------------------------

interface TagsCellProps {
  studentTags: TagDTO[]
  allTags: TagDTO[]
  studentId: number
  onSave: (studentId: number, tagIds: number[]) => void
}

function TagsCell({ studentTags, allTags, studentId, onSave }: TagsCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<TagDTO[]>(studentTags)

  function handleClick() {
    setDraft(studentTags)
    setEditing(true)
  }

  function handleSave() {
    setEditing(false)
    const newIds = draft.map((t) => t.id).sort()
    const oldIds = studentTags.map((t) => t.id).sort()
    if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
      onSave(studentId, newIds)
    }
  }

  if (!editing) {
    return (
      <Box
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
          minHeight: 24,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {studentTags.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          studentTags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              sx={{
                bgcolor: tag.color,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ))
        )}
      </Box>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleSave}>
      <Box>
        <Autocomplete
          multiple
          size="small"
          options={allTags}
          value={draft}
          onChange={(_e, newValue) => setDraft(newValue)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              autoFocus
              placeholder="Select tags..."
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.25 },
                minWidth: 180,
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index })
              return (
                <Chip
                  key={key}
                  label={option.name}
                  size="small"
                  {...chipProps}
                  sx={{
                    bgcolor: option.color,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                    '& .MuiChip-label': { px: 0.75 },
                    '& .MuiChip-deleteIcon': { color: '#fff', fontSize: '0.9rem' },
                  }}
                />
              )
            })
          }
          disableCloseOnSelect
          openOnFocus
          sx={{ minWidth: 180 }}
        />
      </Box>
    </ClickAwayListener>
  )
}

// ---------------------------------------------------------------------------
// Inline Edit Cell
// ---------------------------------------------------------------------------

interface InlineEditCellProps {
  value: string
  studentId: number
  column: SortColumn
  onSave: (studentId: number, field: SortColumn, value: string) => void
}

function InlineEditCell({ value, studentId, column, onSave }: InlineEditCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  function handleClick() {
    setDraft(value)
    setEditing(true)
  }

  function handleSave() {
    setEditing(false)
    if (draft.trim() !== value.trim()) {
      onSave(studentId, column, draft)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditing(false)
      setDraft(value)
    }
  }

  // ---- Join Date: MUI DatePicker with calendar button ----
  if (column === 'joinDate') {
    if (!editing) {
      return (
        <Typography
          variant="body2"
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            '&:hover': { bgcolor: 'action.hover' },
            minHeight: 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {formatDate(value || null)}
        </Typography>
      )
    }

    const dayjsValue: Dayjs | null = value ? dayjs(value) : dayjs()

    return (
      <ClickAwayListener onClickAway={handleSave}>
        <Box>
          <DatePicker
            value={dayjsValue}
            onChange={(newValue: Dayjs | null) => {
              if (newValue && newValue.isValid()) {
                setDraft(newValue.format('YYYY-MM-DD'))
              }
            }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                size: 'small',
                variant: 'standard',
                autoFocus: true,
                onKeyDown: handleKeyDown,
                sx: {
                  '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.25 },
                  minWidth: 150,
                },
              },
              field: {
                clearable: true,
                onClear: () => setDraft(''),
              },
            }}
            slots={{
              openPickerButton: (props) => (
                <IconButton {...props} size="small" sx={{ p: 0.25 }}>
                  <CalendarTodayIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Box>
      </ClickAwayListener>
    )
  }

  // ---- DOB: free-text DateInput with calendar picker ----
  if (column === 'childDob') {
    if (!editing) {
      return (
        <Typography
          variant="body2"
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            borderRadius: 1,
            px: 0.5,
            py: 0.25,
            '&:hover': { bgcolor: 'action.hover' },
            minHeight: 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {formatDate(value)}
        </Typography>
      )
    }

    return (
      <ClickAwayListener onClickAway={handleSave}>
        <Box>
          <DateInput
            value={value}
            onChange={(iso) => setDraft(iso)}
            onKeyDown={handleKeyDown}
            autoFocus
            inputSx={{ fontSize: '0.875rem', py: 0.25 }}
            sx={{ minWidth: 150 }}
          />
        </Box>
      </ClickAwayListener>
    )
  }

  // ---- Text columns ----
  if (!editing) {
    return (
      <Typography
        variant="body2"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
          minHeight: 24,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value || '—'}
      </Typography>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleSave}>
      <Box>
        <TextField
          size="small"
          variant="standard"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.25 },
            minWidth: 100,
          }}
        />
      </Box>
    </ClickAwayListener>
  )
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export default function MasterListPage() {
  const queryClient = useQueryClient()

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>('childName')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<AddStudentInput>(EMPTY_FORM)
  const [draftTags, setDraftTags] = useState<TagDTO[]>([])

  // Drawer state
  const [drawerStudent, setDrawerStudent] = useState<StudentDTO | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Search / filter state
  const [searchText, setSearchText] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set())

  function handleToggleTag(tagId: number) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  function handleClearTags() {
    setSelectedTagIds(new Set())
  }

  // Data
  const { data, isLoading, error } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api.get<StudentDTO[]>('/api/students')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  // Tags data (for the Autocomplete dropdown)
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await api.get<TagDTO[]>('/api/tags')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  // Add mutation
  const addMutation = useMutation({
    mutationFn: async (input: AddStudentInput) => {
      const res = await api.post<StudentDTO>('/api/students', input)
      if (!res.success) throw new Error(res.error)
      // If tags were selected, set them on the new student
      if (draftTags.length > 0) {
        const tagIds = draftTags.map((t) => t.id)
        const tagRes = await api.put<TagDTO[]>(`/api/students/${res.data.id}/tags`, { tagIds })
        if (!tagRes.success) throw new Error(tagRes.error)
      }
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setAddOpen(false)
      setForm(EMPTY_FORM)
      setDraftTags([])
    },
  })

  // Inline update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, input }: { id: number; input: UpdateStudentInput }) => {
      const res = await api.patch<StudentDTO>(`/api/students/${id}`, input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  // Tag save mutation
  const tagSaveMutation = useMutation({
    mutationFn: async ({ id, tagIds }: { id: number; tagIds: number[] }) => {
      const res = await api.put<TagDTO[]>(`/api/students/${id}/tags`, { tagIds })
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  // Handlers
  function handleSort(col: SortColumn) {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
  }

  function handleInlineSave(studentId: number, field: SortColumn, value: string) {
    const payload: UpdateStudentInput = {}
    switch (field) {
      case 'childName':
        payload.childName = value
        break
      case 'parentName':
        payload.parentName = value
        break
      case 'childDob':
        payload.childDob = value
        break
      case 'contactPhone':
        payload.contactPhone = value.trim() || null
        break
      case 'contactEmail':
        payload.contactEmail = value.trim() || null
        break
      case 'joinDate':
        payload.joinDate = value.trim() || null
        break
      default:
        return
    }
    updateMutation.mutate({ id: studentId, input: payload })
  }

  function handleTagsSave(studentId: number, tagIds: number[]) {
    tagSaveMutation.mutate({ id: studentId, tagIds })
  }

  function openDrawer(student: StudentDTO) {
    setDrawerStudent(student)
    setDrawerOpen(true)
  }

  function handleDrawerClose() {
    setDrawerOpen(false)
    // Delay clearing the student so the drawer close animation plays with content
    setTimeout(() => setDrawerStudent(null), 300)
  }

  function handleAddClose() {
    setAddOpen(false)
    setForm(EMPTY_FORM)
    setDraftTags([])
    addMutation.reset()
  }

  const isFormValid =
    form.childName.trim().length > 0 &&
    form.parentName.trim().length > 0 &&
    form.childDob.trim().length > 0

  // Filter + sort students client-side
  const students = useMemo(() => {
    let raw = data ?? []

    // Text filter: match child name, parent name, email, or phone (case-insensitive substring)
    const query = searchText.trim().toLowerCase()
    if (query.length > 0) {
      raw = raw.filter(
        (s) =>
          s.childName.toLowerCase().includes(query) ||
          s.parentName.toLowerCase().includes(query) ||
          (s.contactEmail ?? '').toLowerCase().includes(query) ||
          (s.contactPhone ?? '').toLowerCase().includes(query),
      )
    }

    // Tag filter: student must have ALL selected tags (AND logic)
    if (selectedTagIds.size > 0) {
      raw = raw.filter((s) => {
        const studentTagIds = new Set(s.tags.map((t) => t.id))
        return [...selectedTagIds].every((id) => studentTagIds.has(id))
      })
    }

    // Sort
    return [...raw].sort((a, b) => {
      let aVal: string | number
      let bVal: string | number

      switch (sortColumn) {
        case 'childName':
          aVal = a.childName.toLowerCase()
          bVal = b.childName.toLowerCase()
          break
        case 'parentName':
          aVal = a.parentName.toLowerCase()
          bVal = b.parentName.toLowerCase()
          break
        case 'childDob':
          aVal = a.childDob
          bVal = b.childDob
          break
        case 'ageInMonths':
          aVal = a.ageInMonths
          bVal = b.ageInMonths
          break
        case 'contactPhone':
          aVal = (a.contactPhone ?? '').toLowerCase()
          bVal = (b.contactPhone ?? '').toLowerCase()
          break
        case 'contactEmail':
          aVal = (a.contactEmail ?? '').toLowerCase()
          bVal = (b.contactEmail ?? '').toLowerCase()
          break
        case 'joinDate':
          aVal = a.joinDate ?? ''
          bVal = b.joinDate ?? ''
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortColumn, sortDirection, searchText, selectedTagIds])

  // Render cell value for display (non-editable columns)
  const renderCellValue = useCallback((student: StudentDTO, col: SortColumn): string => {
    switch (col) {
      case 'childName':
        return student.childName
      case 'parentName':
        return student.parentName
      case 'childDob':
        return formatDate(student.childDob)
      case 'ageInMonths':
        return formatAge(student.ageInMonths)
      case 'contactPhone':
        return student.contactPhone ?? '—'
      case 'contactEmail':
        return student.contactEmail ?? '—'
      case 'joinDate':
        return formatDate(student.joinDate)
      default:
        return ''
    }
  }, [])

  // Get raw value for inline editing
  const getRawValue = useCallback((student: StudentDTO, col: SortColumn): string => {
    switch (col) {
      case 'childName':
        return student.childName
      case 'parentName':
        return student.parentName
      case 'childDob':
        return student.childDob
      case 'contactPhone':
        return student.contactPhone ?? ''
      case 'contactEmail':
        return student.contactEmail ?? ''
      case 'joinDate':
        return student.joinDate ?? ''
      default:
        return ''
    }
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Master List{' '}
            {!isLoading && students.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                ({students.length})
              </Typography>
            )}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Add Student
          </Button>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load master list.
          </Alert>
        )}

        {/* Inline update error */}
        {updateMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(updateMutation.error as Error).message}
          </Alert>
        )}

        {/* Filter Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name, parent, email, or phone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            slotProps={{
              input: {
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
            sx={{ width: { xs: '100%', sm: 360 }, mb: 1.25 }}
          />

          {/* Tag filter pills — same styling as row tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            {allTags.map((tag) => {
              const isSelected = selectedTagIds.has(tag.id)
              return (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => handleToggleTag(tag.id)}
                  sx={{
                    bgcolor: isSelected ? tag.color : undefined,
                    color: isSelected ? '#fff' : tag.color,
                    borderColor: tag.color,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 28,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isSelected ? tag.color : `${tag.color}20`,
                    },
                  }}
                />
              )
            })}
            {selectedTagIds.size > 0 && (
              <Chip
                label="Clear"
                size="small"
                variant="outlined"
                onDelete={handleClearTags}
                deleteIcon={<CloseIcon fontSize="small" />}
                sx={{
                  fontSize: '0.75rem',
                  height: 28,
                  color: 'text.secondary',
                  borderColor: 'divider',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48, fontWeight: 700 }} />
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{ fontWeight: 700 }}
                    align={col.align}
                    sortDirection={sortColumn === col.id ? sortDirection : false}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortColumn === col.id}
                        direction={sortColumn === col.id ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? [1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton variant="circular" width={24} height={24} />
                      </TableCell>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : students.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell sx={{ width: 48, p: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => openDrawer(student)}
                          color="primary"
                          title={`View ${student.childName}`}
                        >
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id} align={col.align}>
                          {col.id === 'tags' ? (
                            <TagsCell
                              studentTags={student.tags}
                              allTags={allTags}
                              studentId={student.id}
                              onSave={handleTagsSave}
                            />
                          ) : col.editable ? (
                            <InlineEditCell
                              value={getRawValue(student, col.id)}
                              studentId={student.id}
                              column={col.id}
                              onSave={handleInlineSave}
                            />
                          ) : (
                            <Typography variant="body2">{renderCellValue(student, col.id)}</Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

              {!isLoading && students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {data && data.length > 0
                        ? (searchText.trim().length > 0 || selectedTagIds.size > 0)
                          ? 'No students match the current search or filters.'
                          : 'No students in the master list. Add your first student to get started.'
                        : 'No students in the master list. Add your first student to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile FAB */}
        <Tooltip title="Add Student">
          <Fab
            color="primary"
            onClick={() => setAddOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24, display: { xs: 'flex', sm: 'none' } }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>

        {/* Add Student Dialog */}
        <Dialog open={addOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add Student</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Child Name"
                value={form.childName}
                onChange={(e) => setForm({ ...form, childName: e.target.value })}
                required
                fullWidth
              />
              <DateInput
                label="Date of Birth"
                value={form.childDob}
                onChange={(iso) => setForm({ ...form, childDob: iso })}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Parent Name"
                value={form.parentName}
                onChange={(e) => setForm({ ...form, parentName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Phone"
                value={form.contactPhone ?? ''}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                fullWidth
              />
              <TextField
                label="Email"
                value={form.contactEmail ?? ''}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                fullWidth
              />
              <DatePicker
                label="Join Date"
                value={form.joinDate ? dayjs(form.joinDate) : null}
                onChange={(newValue: Dayjs | null) =>
                  setForm({ ...form, joinDate: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '' })
                }
                format="MM/DD/YYYY"
                slotProps={{
                  textField: { fullWidth: true },
                  field: { clearable: true },
                }}
              />
              <Autocomplete
                multiple
                options={allTags}
                value={draftTags}
                onChange={(_e, newValue) => setDraftTags(newValue)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Select tags..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index })
                    return (
                      <Chip
                        key={key}
                        label={option.name}
                        size="small"
                        {...chipProps}
                        sx={{
                          bgcolor: option.color,
                          color: '#fff',
                          fontWeight: 600,
                          '& .MuiChip-deleteIcon': { color: '#fff' },
                        }}
                      />
                    )
                  })
                }
                disableCloseOnSelect
              />
              <TextField
                label="Notes"
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              {addMutation.isError && (
                <Alert severity="error">{(addMutation.error as Error).message}</Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => addMutation.mutate(form)}
              disabled={!isFormValid || addMutation.isPending}
            >
              {addMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Student Drawer */}
        <StudentDrawer
          key={drawerStudent?.id ?? 'empty'}
          student={drawerStudent}
          open={drawerOpen}
          onClose={handleDrawerClose}
        />
      </Box>
    </LocalizationProvider>
  )
}